import './CarouselList.scss';

import { Carousel2, ListView, Typography } from '@modulor/react';
import DepositIcon from 'assets/DepositIcon/DepositIcon';
import clx from 'classnames';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { ConditionalContentListCell } from 'Modulor/components/ConditionalContentListCell';
import { MethodLogo } from 'Modulor/components/MethodLogo/MethodLogo';
import type { FC, MouseEvent } from 'react';
import React, { memo, useEffect } from 'react';
import {
  getClickStreamEvent,
  getEventAnalytic,
  useGetClickStreamCashierContext,
  useGetClickStreamPayGroupCashierContext,
} from 'services/clickstream';
import type { FlowTypes } from 'store/constants';
import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCarouselViewOff } from 'store/slices/carousel';
import {
  cleanCurrentMethod as cleanDepositMethod,
  paygroupCategoriesSelector,
  setSkippingAutoSelectMethod,
} from 'store/slices/deposit';
import {
  amountSelector,
  amountTouchedSelector,
  logoSizesSelector,
  platformSelector,
  selectedPayGroupIdSelector,
  userBalanceSelector,
} from 'store/slices/global';
import {
  isShowGroupingMethodsInCarouselSelector,
  selectedCategorySelector,
  setUserCategorySelection,
} from 'store/slices/methodGrouping';
import { cleanCurrentMethod as cleanWithdrawalMethod, payoutGroupCategoriesSelector } from 'store/slices/withdrawal';
import { getInitialTheme, isDepositFlowType, updateLogoUrlViaTheme, widgetLocations } from 'utils';

import { formMethodSubTitle } from '../Deposit/utils';
import { useNavigateWithSearch } from '../hooks';
import { formMethodSubTitleTranslationKeys } from '../payUtils';
import { CarouselDepositSelectStrategy } from './selectStrategy/CarouselDepositSelectStrategy';
import { CarouselSelectHandler } from './selectStrategy/CarouselSelectHandler';
import { CarouselWithdrawalSelectStrategy } from './selectStrategy/CarouselWithdrawalSelectStrategy';
import { storeAmount } from './utils';

interface CarouselListProps {
  list: (UIPaymentGroups | UIPayoutGroups)[];
  flowType: FlowTypes;
  onMethodSelect?: (methodId: number) => void;
}
const clickstream = getEventAnalytic();

const CarouselListComponent: FC<CarouselListProps> = (props) => {
  const { list, flowType, onMethodSelect } = props;
  const { translate } = useSkeletonTranslation();
  const initialTheme = getInitialTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigateWithSearch();
  const logoSizes = useAppSelector(logoSizesSelector);
  const selectedMethodId = useAppSelector(selectedPayGroupIdSelector);
  const platform = useAppSelector(platformSelector);
  const storedAmount = useAppSelector(amountSelector);
  const amountTouched = useAppSelector(amountTouchedSelector);
  const userBalance = useAppSelector(userBalanceSelector);
  const isGroupingMethodsInCarousel = useAppSelector(isShowGroupingMethodsInCarouselSelector);
  const isDeposit = isDepositFlowType(flowType);
  const depositCategories = useAppSelector(paygroupCategoriesSelector);
  const withdrawalCategories = useAppSelector(payoutGroupCategoriesSelector);
  const paygroupCategories = isDeposit ? depositCategories : withdrawalCategories;
  const isCategoryScrollLocked = paygroupCategories?.is_category_scroll_locked || false;
  const selectedCategory = useAppSelector(selectedCategorySelector);
  const payGroupContext = useGetClickStreamPayGroupCashierContext(flowType);
  const cashierContext = useGetClickStreamCashierContext(flowType);
  const Strategy = isDeposit ? CarouselDepositSelectStrategy : CarouselWithdrawalSelectStrategy;

  const selectMethodHandler = new CarouselSelectHandler(new Strategy());
  const selectMethod = (m: UIPaymentGroups | UIPayoutGroups) => (e: MouseEvent) => {
    if (m.id === selectedMethodId) return;

    if (!isCategoryScrollLocked) {
      storeAmount({
        storedAmount,
        amountTouched,
        dispatch,
        m,
        userBalance,
        flowType,
        isInCarousel: isGroupingMethodsInCarousel,
      });
    }

    dispatch(setSkippingAutoSelectMethod());
    selectMethodHandler.handle(
      {
        method: m,
        platform,
        dispatch,
        navigate,
        flowType,
        currentPayGroupId: selectedMethodId ?? undefined,
      },
      e,
    );

    // Save user selection for the current category (used by MethodGroupingController)
    if (selectedCategory) {
      dispatch(
        setUserCategorySelection({
          category_name: selectedCategory,
          payGroupId: m.id,
        }),
      );
    }

    onMethodSelect?.(m.id);
    const event = getClickStreamEvent.demetr_carousel_method_click(m.name);
    clickstream.push(event, [cashierContext, payGroupContext]);
  };
  const goToAllMethods = () => {
    dispatch(setSkippingAutoSelectMethod());
    navigate(isDeposit ? widgetLocations.deposit : widgetLocations.withdrawal);
    dispatch(isDeposit ? cleanDepositMethod() : cleanWithdrawalMethod());
    dispatch(setCarouselViewOff());
    const event = getClickStreamEvent.demetr_carousel_all_methods_click;
    clickstream.push(event, [cashierContext, payGroupContext]);
  };

  useEffect(() => {
    const event = getClickStreamEvent.demetr_carousel_view;
    clickstream.push(event, [cashierContext, payGroupContext]);
  }, []);

  if (!list.length) {
    return null;
  }

  const selectedMethodListItem = list.find((m) => m.id === selectedMethodId) ?? list[0];

  const pickLostProfitInfo = (m: UIPaymentGroups | UIPayoutGroups) =>
    'lostProfitInfo' in m ? (m.lostProfitInfo ?? undefined) : undefined;
  const pickIsIncludeBonus = (m: UIPaymentGroups | UIPayoutGroups) =>
    'isIncludeBonus' in m ? Boolean(m.isIncludeBonus) : undefined;

  const subtitleNode = formMethodSubTitle({
    t: translate,
    processingTime: selectedMethodListItem.processingTimeTranslationKey,
    fee: selectedMethodListItem.merchantUserFee || selectedMethodListItem.fee || {},
    lostProfitInfo: pickLostProfitInfo(selectedMethodListItem),
    isIncludeBonus: pickIsIncludeBonus(selectedMethodListItem),
    limits: selectedMethodListItem.limits,
    currency: selectedMethodListItem.currencyAlpha3,
  });

  const hasSubtitle = !!subtitleNode.trim();

  return (
    <section
      data-testid="carousel-list"
      className={`carousel-list${isGroupingMethodsInCarousel ? ' carousel-list--custom-margin' : ''}`}
    >
      <Carousel2>
        {list.map((m) => (
          <ListView key={`carousel-item-key-${m.id}`} className="carousel-list__item" data-testid="carousel-list__item">
            <ConditionalContentListCell
              className={clx('methods__item', 'carousel-list__item-cell', {
                'lost-profit': 'isLostProfit' in m ? m.isLostProfit : null,
                selected: m.id === selectedMethodId,
              })}
              data-translation-key={formMethodSubTitleTranslationKeys({
                processingTime: m.processingTimeTranslationKey,
                fee: m.merchantUserFee || {},
                lostProfitInfo: pickLostProfitInfo(m),
                isIncludeBonus: pickIsIncludeBonus(m),
                limits: m.limits,
              })}
              onClick={selectMethod(m)}
              icon={
                <MethodLogo
                  src={updateLogoUrlViaTheme(m.logoUri, initialTheme)}
                  alt={m.displayName}
                  width={logoSizes.width}
                  height={logoSizes.height}
                />
              }
            />
          </ListView>
        ))}
        {!isGroupingMethodsInCarousel && (
          <ListView key="carousel-item-key-all" className="carousel-list__item" data-testid="carousel-list__item">
            <ConditionalContentListCell
              className="methods__item carousel-list__item--show-all"
              text={translate('PH.BUTTON.MORE')}
              onClick={goToAllMethods}
              icon={<DepositIcon />}
            />
          </ListView>
        )}
      </Carousel2>
      {selectedMethodListItem.id === selectedMethodId && (
        <div
          className={clx('carousel-list__selected-method', {
            'carousel-list__selected-method--no-subtitle': !hasSubtitle,
          })}
        >
          <Typography
            variant="subhead-semibold"
            color="text-title"
            tag="h4"
            className="carousel-list__selected-method-title"
          >
            {translate(selectedMethodListItem.displayName) || ''}
          </Typography>
          {hasSubtitle && (
            <Typography variant="caption-1-regular" color="text-body" tag="p">
              {subtitleNode}
            </Typography>
          )}
        </div>
      )}
    </section>
  );
};

export const CarouselList = memo(CarouselListComponent);
